import moment from "moment";
import {
	_expiredListingsFilters,
	_onMarketListingsFilters,
	EXPIRED_LISTINGS_MAX_DATE_RANGE
} from "./filters";
import { _capitalizeText, _tranformTagsQuery, expiredListingsFL } from "./utils";

const OFF_MARKET_TYPE_TO_FIELD = {
	tom: "last_listed_sale_tom_date_formatted",
	pom: "last_listed_sale_pom_date_formatted",
	expired: "last_listed_sale_expired_date_formatted"
};

const COMMON_FQ = [
	"-offering_verified_status:stalled",
	"deleted:false",
	"-offering_unit_abandoned:true",
	"owner:?*",
	`-(ownership_type:(condo* coop co-op) AND address_2_normalized:"")`
];

export function getExpiredListingsBaseFQ(searchMode) {
	if (searchMode === "on_market") {
		return [
			...COMMON_FQ,
			"current_status:active",
			`last_listed_sale_date_all:[${Math.round(Date.now() / 1000) - 94608000} TO *]`
		];
	}
	return [
		...COMMON_FQ,
		`-current_status:(active "in contract" pending "active under contract" "coming soon")`,
		`(last_listed_sale_tom_date_formatted:[${EXPIRED_LISTINGS_MAX_DATE_RANGE} TO NOW] OR last_listed_sale_pom_date_formatted:[${EXPIRED_LISTINGS_MAX_DATE_RANGE} TO NOW] OR last_listed_sale_expired_date_formatted:[${EXPIRED_LISTINGS_MAX_DATE_RANGE} TO NOW])`
	];
}

export function getActiveExpiredListingsFilters(searchMode) {
	return searchMode === "on_market" ? _onMarketListingsFilters : _expiredListingsFilters;
}

/**
 * Build the Solr query pieces for the Expired Listings search.
 *
 * Pure function — takes a context object, returns { q, fq, sort, fl }. Used by:
 *   - ExpiredListingsSearchTable (interactive page)
 *   - Backend expired-listing-digest job (daily email)
 *
 * To run a digest, the job should construct subFilters with:
 *   off_market_type: <subscription.offMarketTypes>   // e.g. ['expired','pom']
 *   min_off_market_date: <subscription.lastRunAt ISO>
 *   max_off_market_date: <now ISO>
 * …and pass the rest of the user's saved filters through unchanged.
 */
export function buildExpiredListingsQuery({
	searchMode = "off_market",
	subFilters = {},
	buildingStages = ["all"],
	searchText = "",
	searchTags = [],
	orderBy = "",
	order = "desc",
	defaultBuildingQ = "",
	fl = expiredListingsFL
} = {}) {
	const activePropertyFilter = getActiveExpiredListingsFilters(searchMode).filter(
		f => !f.filter_type
	);

	let q = "*:*";
	const unitFq = getExpiredListingsBaseFQ(searchMode).slice();

	const hasBuilding =
		searchTags && searchTags.filter(tag => tag.type === "building").length > 0;
	if (hasBuilding) {
		unitFq.push(`-address_2_normalized:""`);
	}

	const locQuery = searchTags ? _tranformTagsQuery(searchTags, "building") : null;
	if (locQuery) {
		q += q.length > 0 ? " AND " : "";
		q += `${locQuery}`;
	}

	// Off-market date + type combo (off-market mode only). We intercept here so
	// that the saved off_market_type + min/max_off_market_date produce a single
	// (field:[min TO max] OR …) clause, bypassing the generic handlers below.
	const offMarketSkipKeys = new Set();
	if (searchMode === "off_market" && subFilters) {
		const minDate = subFilters["min_off_market_date"];
		const maxDate = subFilters["max_off_market_date"];
		const offMarketTypeVal = subFilters["off_market_type"];

		if ((minDate || maxDate) && offMarketTypeVal && offMarketTypeVal.length > 0) {
			const selectedTypes = Array.isArray(offMarketTypeVal)
				? offMarketTypeVal
				: [offMarketTypeVal];
			const fields = selectedTypes
				.map(t => OFF_MARKET_TYPE_TO_FIELD[t])
				.filter(Boolean);

			const minStr = minDate ? moment(minDate).toISOString() : "*";
			const maxStr = maxDate ? moment(maxDate).toISOString() : "*";

			if (fields.length > 0) {
				const queryParts = fields.map(f => `${f}:[${minStr} TO ${maxStr}]`);
				q += q.length > 0 ? " AND " : "";
				q += `(${queryParts.join(" OR ")})`;
				offMarketSkipKeys.add("min_off_market_date");
				offMarketSkipKeys.add("max_off_market_date");
				offMarketSkipKeys.add("off_market_type");
			}
		}
	}

	// General subFilters (multi_filter / text_filter / plain)
	if (subFilters && Object.keys(subFilters).length > 0) {
		Object.keys(subFilters).forEach(key => {
			if (offMarketSkipKeys.has(key)) return;
			const subFilterVal = subFilters[key];
			if (!(subFilterVal && subFilterVal !== "")) return;

			const theFilter = activePropertyFilter.filter(f => f.search_key === key);
			const f0 = theFilter[0];

			if (f0 && f0.multi_filter) {
				const qVal =
					theFilter.length > 0 &&
					f0.values.length > 0 &&
					f0.values.filter(v => subFilterVal.indexOf(v.filter) > -1).length > 0
						? f0.values
								.filter(v => subFilterVal.indexOf(v.filter) > -1)
								.map(v => v.search_value)
						: null;
				if (qVal !== null) {
					if (f0.target === "fq" && f0.join_query) {
						unitFq.push(`${f0.join_query}(${qVal.join(" OR ")})`);
					} else {
						q += q.length > 0 ? " AND " : "";
						q += `(${key}:(${qVal.join(" ")}))`;
					}
				}
			} else if (f0 && f0.text_filter) {
				let qVal = null;
				if (f0.search_fields) {
					qVal = subFilterVal
						.split(" ")
						.map(term => {
							return (
								"(" +
								f0.search_fields
									.map(field => {
										return `${field}:(*${term.toLowerCase()}* *${term.toUpperCase()}* *${_capitalizeText(
											term
										)}* *${term}*)`;
									})
									.join(" OR ") +
								")"
							);
						})
						.join(" AND ");
				} else {
					qVal = subFilterVal
						.split(" ")
						.map(term => {
							return `(${key}:(*${term.toLowerCase()}* *${term.toUpperCase()}* *${_capitalizeText(
								term
							)}* *${term}*))`;
						})
						.join(" AND ");
				}
				if (qVal !== null) {
					q += q.length > 0 ? " AND " : "";
					q += `(${qVal})`;
				}
			} else {
				const qVal =
					theFilter.length > 0 &&
					f0.values &&
					f0.values.length > 0 &&
					f0.values.filter(v => v.filter === subFilterVal).length > 0
						? f0.values.filter(v => v.filter === subFilterVal)[0].search_value
						: null;
				if (qVal !== null) {
					if (f0.target === "fq") {
						unitFq.push(qVal);
					} else {
						q += q.length > 0 ? " AND " : "";
						q += `(${key}:${qVal})`;
					}
				}
			}
		});
	}

	// Date filters and price filters (placeholder substitution)
	const dateQ = {};
	const dateReplaceKeys = {};

	if (subFilters && Object.keys(subFilters).length > 0) {
		Object.keys(subFilters).forEach(key => {
			if (offMarketSkipKeys.has(key)) return;
			const subFilterVal = subFilters[key];
			if (!(subFilterVal && subFilterVal !== "")) return;

			const dateArrayFilter = activePropertyFilter.filter(
				f =>
					f.date_filter &&
					Array.isArray(f.search_key) &&
					f.search_key.filter(sk => sk === key).length > 0
			);
			if (dateArrayFilter.length > 0) {
				const filterKey = dateArrayFilter[0].filter_key;
				dateReplaceKeys[filterKey] = dateReplaceKeys[filterKey]
					? [...dateReplaceKeys[filterKey], ...dateArrayFilter[0].search_key]
					: [...dateArrayFilter[0].search_key];
				const regex = new RegExp(`\\{${key}\\}`, "g");
				if (dateQ[filterKey]) {
					dateQ[filterKey] = dateQ[filterKey].replace(
						regex,
						moment(subFilterVal).toISOString()
					);
				} else {
					dateQ[filterKey] = dateArrayFilter[0].search_value
						.map(s => s.replace(regex, moment(subFilterVal).toISOString()))
						.join(" OR ");
				}
			}

			const priceArrayFilter = activePropertyFilter.filter(
				f =>
					f.price_filter &&
					Array.isArray(f.search_key) &&
					f.search_key.filter(sk => sk === key).length > 0
			);
			if (priceArrayFilter.length > 0) {
				const filterKey = priceArrayFilter[0].filter_key;
				if (!dateReplaceKeys[filterKey]) {
					dateReplaceKeys[filterKey] = [...priceArrayFilter[0].search_key];
				}
				const priceRegex = new RegExp(`\\{${key}\\}`, "g");
				if (dateQ[filterKey]) {
					dateQ[filterKey] = dateQ[filterKey].replace(priceRegex, subFilterVal);
				} else {
					dateQ[filterKey] = priceArrayFilter[0].search_value
						.map(s => s.replace(priceRegex, subFilterVal))
						.join(" OR ");
				}
			}
		});
	}

	if (dateQ && Object.keys(dateQ).length > 0) {
		Object.keys(dateQ).forEach(filterKey => {
			const theFilter = activePropertyFilter.filter(
				f => f.filter_key && f.filter_key === filterKey
			)[0];
			if (dateReplaceKeys[filterKey]) {
				dateReplaceKeys[filterKey].forEach(key => {
					const regex = new RegExp(`\\{${key}\\}`, "g");
					if (theFilter && theFilter.date_filter) {
						dateQ[filterKey] = dateQ[filterKey].replace(regex, "*");
					} else if (key.indexOf("min") >= 0) {
						dateQ[filterKey] = dateQ[filterKey].replace(regex, "0");
					} else {
						dateQ[filterKey] = dateQ[filterKey].replace(regex, "*");
					}
				});
			}
			dateQ[filterKey] = dateQ[filterKey].replace(/ u=\*/g, "");

			if (theFilter && theFilter.target === "fq") {
				unitFq.push(dateQ[filterKey]);
			} else {
				q += q.length > 0 ? " AND " : "";
				q += `(${dateQ[filterKey]})`;
			}
		});
	}

	// Building stage join
	let buildingQ = defaultBuildingQ || "";
	if (buildingStages && buildingStages.length > 0 && buildingStages.indexOf("all") < 0) {
		buildingQ += buildingQ.length > 0 ? " AND " : "";
		buildingQ += `building_status:("${buildingStages.join('" OR "')}")`;
	}
	unitFq.push(
		`{!join from=alternate_keys fromIndex=we3-buildings to=building}${buildingQ}`
	);

	// Free-text search
	if (searchText && searchText.length > 0) {
		const text = searchText.replace(/ /g, "*").toLowerCase();
		const textCaps = _capitalizeText(searchText.toLowerCase(), false).replace(
			/ /g,
			"*"
		);
		q += q.length > 0 ? " AND " : "";
		q += `(building_name:(*${text}* OR *${textCaps}*)  OR display_full_street_address:*${text}* OR neighborhood:*${text}*)`;
	}
	q = q.length > 0 ? q : "*:*";

	// Sort
	let sort =
		searchMode === "on_market"
			? "last_listed_sale_date_all asc"
			: "last_listed_sale_expired_date_formatted desc, last_listed_sale_pom_date_formatted desc, last_listed_sale_tom_date_formatted desc";
	if (orderBy && orderBy.length > 0) {
		if (orderBy === "address_2_normalized") {
			sort = `floor ${order}, ${orderBy} ${order}`;
		} else if (searchMode === "off_market" && orderBy === "off_market_date") {
			sort = `last_listed_sale_expired_date_formatted ${order}, last_listed_sale_pom_date_formatted ${order}, last_listed_sale_tom_date_formatted ${order}`;
		} else {
			sort = `${orderBy} ${order}`;
		}
	}

	return { q, fq: unitFq, sort, fl, autocompleteFQ: buildingQ };
}
